import { useEffect, useRef, useState } from "react";
import { Clock, Zap } from "lucide-react";

/**
 * Countdown timer used by every Practice module.
 * - Starts when `running` becomes true, ticks every 1s.
 * - Fires `onTimeUp()` exactly once when it hits zero.
 * - Renders a big MM:SS with color-shifting urgency (last 60s red pulse).
 * - Purely visual — parent still owns the actual "submit at 0" logic.
 */
export default function CountdownTimer({
  durationSec = 15 * 60,
  running = false,
  onTimeUp,
  onTick,           // (secondsLeft) => void — for parents that need live seconds
  testId = "countdown",
}) {
  const [left, setLeft] = useState(durationSec);
  const firedRef = useRef(false);
  const timerRef = useRef(null);

  // Reset when duration changes
  useEffect(() => {
    setLeft(durationSec);
    firedRef.current = false;
  }, [durationSec]);

  useEffect(() => {
    if (!running) return;
    firedRef.current = false;
    timerRef.current = setInterval(() => {
      setLeft((s) => {
        const nxt = s - 1;
        if (onTick) onTick(nxt);
        if (nxt <= 0 && !firedRef.current) {
          firedRef.current = true;
          clearInterval(timerRef.current);
          try { onTimeUp?.(); } catch { /* ignore */ }
          return 0;
        }
        return nxt;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const mm = String(Math.max(0, Math.floor(left / 60))).padStart(2, "0");
  const ss = String(Math.max(0, left % 60)).padStart(2, "0");
  const urgent = left <= 60 && running;
  const warning = left <= 120 && !urgent && running;

  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center gap-3 px-5 h-14 rounded-xl border-2 font-mono transition-colors ${
        urgent
          ? "border-brand-crimson bg-brand-crimson/10 text-brand-crimson animate-pulse"
          : warning
            ? "border-brand-gold bg-brand-gold/10 text-brand-gold"
            : "border-border bg-secondary/40 text-foreground"
      }`}
    >
      {urgent ? <Zap className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
      <div className="leading-none">
        <div className="text-[9px] uppercase tracking-[0.3em] font-bold opacity-70 mb-0.5">
          {urgent ? "Hurry!" : warning ? "Almost up" : "Time left"}
        </div>
        <div className="text-2xl sm:text-3xl font-black tracking-tight tabular-nums">
          {mm}:{ss}
        </div>
      </div>
    </div>
  );
}
